import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import { TransitionGroup } from 'react-transition-group';

import Fade from '../transitions/Fade';
import Type from '../Typography';
import PageLinks from '../pagination/PageLinks';
import { ensureArray } from '../utils';

import Close from './Close';
import StatusIcon from './StatusIcon';
import styles from './MessageBar.less';

const cxb = cx.bind(styles);
const hasPages = (x) => x && x >= 2;

const Message = React.forwardRef(({ message }, ref) => {
  const { type, content } = message || {};
  return !message ? null : (
    <div className={cxb('message', type)} ref={ref}>
      <span className={cxb('centerer')}>
        <StatusIcon type={type} small />
        <Type.SubText1 as="span" className={cxb('content')}>
          {content}
        </Type.SubText1>
      </span>
    </div>
  );
});

/**
 * **Access:**
 * ```js
 * import { MessageBar } from '@iss/client-react-components'
 * ```
 *
 * A notification bar. The composing component/store is responsible for adding/removing items.
 *
 * This takes an array of objects. Each object should have a `type` and `content` properties. If you supply a
 * `dismiss` callback, and X will be rendered to allow dismissal. The composing (store connected) component should
 * handle state.
 *
 */
export default class MessageBar extends React.Component {
  static propTypes = {
    /** force the bar to fill the width of the screen overflowing the container */
    stretch: PropTypes.bool,
    /** The list of messages, the bar will not render with an empty list. */
    messages: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['info', 'attention', 'warning', 'success', 'message']).isRequired,
        content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequred,
        dismiss: PropTypes.func,
      })
    ),
  };

  state = {
    index: 0,
  };

  componentDidUpdate(prevProps, prevState) {
    /* istanbul ignore else */
    if (prevProps.messages !== this.props.messages) {
      this.setState({ index: 0 });
    }
  }

  render() {
    const {
      state: { index },
      props: { stretch = false, messages },
    } = this;

    const list = ensureArray(messages);
    const { length: count } = list;
    const empty = count === 0;

    const message = list[index || 0];
    const { type, dismiss } = message || {};

    return (
      <Fade appear in={!empty}>
        <div className={cxb('root', type, { stretch, empty, pages: hasPages(count) })}>
          <TransitionGroup component={null}>
            <Fade direction="left-cont" key={index}>
              <Message message={message} />
            </Fade>
          </TransitionGroup>
          {dismiss && <Close className={cxb('close')} onClick={dismiss} />}
          {this.renderPager(index, count)}
        </div>
      </Fade>
    );
  }

  renderPager(current, count) {
    return !hasPages(count) ? null : (
      <PageLinks
        classes={{
          container: cxb('dots'),
          dot: cxb('dot'),
          active: cxb('active'),
        }}
        pageCount={count}
        currentPage={current + 1}
        onPageChange={this.handleJump}
      />
    );
  }

  handleJump = (page) => {
    const index = page - 1;
    this.setState({ index });
  };
}
